import type { FC, ReactNode } from 'react'

import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'
import { FOOTER_LINKS } from '@/constants/landing'

const LINK_STYLE = 'rounded-sm decoration-primary decoration-[3px] underline-offset-[6px] hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground'

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-background'>
            <div className='site-container pt-20 pb-8 md:pt-28'>
                <div className='grid gap-12 md:grid-cols-12'>
                    <div className='md:col-span-6 lg:col-span-5'>
                        <p className='font-heading text-[clamp(1.75rem,3vw,2.75rem)] leading-[1.02] font-extrabold tracking-[-0.02em] wdth-90'>
                            Brands you can’t walk past. Made loud in Brooklyn since 2014.
                        </p>

                        <a href='/#contact' className={buttonStyles({ className: 'mt-8' })}>
                            Start a project
                            <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover/button:rotate-45' />
                        </a>
                    </div>

                    <nav aria-label='Footer' className='md:col-span-3 lg:col-span-2 lg:col-start-8'>
                        <p className='font-mono text-xs font-medium tracking-[0.2em] text-muted-foreground uppercase'>
                            Explore
                        </p>

                        <ul className='mt-4 space-y-2'>
                            {FOOTER_LINKS.map(link => (
                                <li key={link.href}>
                                    <a href={link.href} className={`text-lg font-semibold ${LINK_STYLE}`}>
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='md:col-span-3'>
                        <p className='font-mono text-xs font-medium tracking-[0.2em] text-muted-foreground uppercase'>
                            Studio
                        </p>

                        <address className='mt-4 text-lg leading-relaxed not-italic'>
                            Brooklyn, New York
                            <br />
                            Monday to Friday, 9am to 6pm ET
                        </address>

                        <p className='mt-5 inline-flex items-center gap-2 rounded-full bg-foreground px-3 py-1.5 font-mono text-xs tracking-[0.14em] text-background uppercase'>
                            <span aria-hidden='true' className='size-2 rounded-full bg-pop-mint' />
                            Booking spring 2027
                        </p>
                    </div>
                </div>

                <svg
                    viewBox='0 0 1000 196'
                    aria-hidden='true'
                    focusable='false'
                    className='mt-16 block w-full text-foreground md:mt-24'
                >
                    <text
                        x='0'
                        y='182'
                        textLength='1000'
                        lengthAdjust='spacingAndGlyphs'
                        className='fill-current font-heading font-black'
                        style={{ fontSize: 240, fontVariationSettings: '"wdth" 100' }}
                    >
                        WALLOP
                    </text>
                </svg>

                <div className='mt-6 flex flex-col gap-4 border-t-2 border-foreground pt-6 font-mono text-xs tracking-[0.16em] uppercase sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> Wallop Studio. All rights reserved.
                    </p>

                    <a href='#main' className={`inline-flex items-center gap-2 self-start sm:self-auto ${LINK_STYLE}`}>
                        Back to top
                        <ArrowUp aria-hidden='true' className='size-4' />
                    </a>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
